import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Top-10 crypto-to-Binance USDT pair mapping. POL replaced MATIC on Binance in late 2024
// but we send the user-facing label "MATIC" to keep continuity.
export const STREAMED_COINS = [
  { symbol: "BTC", name: "Bitcoin", binance: "btcusdt" },
  { symbol: "ETH", name: "Ethereum", binance: "ethusdt" },
  { symbol: "SOL", name: "Solana", binance: "solusdt" },
  { symbol: "BNB", name: "BNB", binance: "bnbusdt" },
  { symbol: "XRP", name: "XRP", binance: "xrpusdt" },
  { symbol: "ADA", name: "Cardano", binance: "adausdt" },
  { symbol: "DOGE", name: "Dogecoin", binance: "dogeusdt" },
  { symbol: "DOT", name: "Polkadot", binance: "dotusdt" },
  { symbol: "LINK", name: "Chainlink", binance: "linkusdt" },
  { symbol: "MATIC", name: "Polygon", binance: "polusdt" },
];

const STREAM_URL = `wss://stream.binance.com:9443/stream?streams=${STREAMED_COINS.map(
  (c) => `${c.binance}@ticker`
).join("/")}`;

const MAX_BACKOFF_MS = 32000;

/**
 * Connect to Binance combined ticker stream and expose a live map of
 * { symbol → { price, change24h, volume24h, high24h, low24h, ts } }.
 * Auto-reconnects with exponential backoff + jitter.
 *
 * Also fetches a one-shot REST snapshot from /api/crypto/top10 on mount so the
 * UI renders prices even when the WebSocket can't connect (geo-block,
 * corporate firewall, strict CSP). Live WS messages overlay the snapshot.
 */
export default function useBinanceTicker() {
  const [prices, setPrices] = useState({});
  const [status, setStatus] = useState("connecting");
  const wsRef = useRef(null);
  const attemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const closingRef = useRef(false);

  // One-shot REST snapshot prefill
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/crypto/top10`)
      .then((res) => {
        if (cancelled) return;
        const items = res.data?.items || [];
        if (!items.length) return;
        setPrices((prev) => {
          const next = { ...prev };
          for (const it of items) {
            // Only seed if WS hasn't already populated this symbol
            if (!next[it.symbol]) {
              next[it.symbol] = {
                symbol: it.symbol,
                name: it.name,
                price: it.price,
                change24h: it.change24h,
                volume24h: it.volume24h,
                high24h: it.high24h,
                low24h: it.low24h,
                ts: it.ts,
              };
            }
          }
          return next;
        });
      })
      .catch(() => {
        /* snapshot is best-effort; WS will fill in if it connects */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleMessage = useCallback((raw) => {
    try {
      const parsed = JSON.parse(raw);
      const t = parsed?.data;
      const stream = parsed?.stream;
      if (!t || !stream) return;
      const binance = stream.split("@")[0];
      const coin = STREAMED_COINS.find((c) => c.binance === binance);
      if (!coin) return;
      setPrices((prev) => ({
        ...prev,
        [coin.symbol]: {
          symbol: coin.symbol,
          name: coin.name,
          price: parseFloat(t.c),
          change24h: parseFloat(t.P),
          volume24h: parseFloat(t.q), // quote-asset volume, USD-ish
          high24h: parseFloat(t.h),
          low24h: parseFloat(t.l),
          ts: t.E,
        },
      }));
    } catch (_) {
      /* ignore malformed frame */
    }
  }, []);

  const connect = useCallback(() => {
    if (typeof WebSocket === "undefined") return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    setStatus("connecting");
    let ws;
    try {
      ws = new WebSocket(STREAM_URL);
    } catch (e) {
      setStatus("error");
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      attemptRef.current = 0;
      setStatus("connected");
    };
    ws.onmessage = (e) => handleMessage(e.data);
    ws.onerror = () => setStatus("error");
    ws.onclose = () => {
      if (closingRef.current) return;
      setStatus("disconnected");
      scheduleReconnect();
    };
  }, [handleMessage]);

  const scheduleReconnect = useCallback(() => {
    if (closingRef.current) return;
    if (reconnectTimerRef.current) return;
    const attempt = attemptRef.current;
    const base = Math.min(1000 * 2 ** attempt, MAX_BACKOFF_MS);
    const jitter = Math.random() * 0.25 * base;
    const delay = base + jitter;
    attemptRef.current = attempt + 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    closingRef.current = false;
    connect();
    return () => {
      closingRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      try {
        wsRef.current && wsRef.current.close();
      } catch (_) {
        /* noop */
      }
    };
  }, [connect]);

  return { prices, status };
}
